import { EntityLiveState, EntityType } from '@/__generated__/API';
import { GraphqlClient } from '@/libs/amplify/types';
import type { Schema } from '@/schema';
import {
  DefaultError,
  QueryKey,
  UseSuspenseQueryOptions
} from '@tanstack/react-query';

export type BrowseEntities = Schema['browseEntities']['returnType'];
export type SearchResults = Schema['searchResults']['type'];

export type BrowseEntitiesOptions = {
  liveState?: EntityLiveState;
  tags?: string[];
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
  numResults?: number;
  pageStart?: number;
  userEntitlements?: string[];
  includePurchasedOnly?: boolean;
} & Pick<Schema['browseEntities']['args'], 'tagsOperator'>;
/**
 * Constructs the query key used for the `browseEntities` query.
 *
 * @param entityType - The type of entity to browse.
 * @param options - Additional optional parameters for filtering and sorting the query.
 * @returns A query key array.
 */
function browseEntitiesQueryKey(
  entityType: EntityType,
  options: BrowseEntitiesOptions = {}
): QueryKey {
  const {
    liveState,
    tags,
    tagsOperator,
    sortField,
    sortDirection,
    userEntitlements,
    numResults = 12,
    pageStart = 0,
    includePurchasedOnly = false
  } = options;
  return [
    'BrowseEntities',
    entityType,
    liveState,
    tags,
    sortField,
    sortDirection,
    userEntitlements,
    numResults,
    pageStart,
    includePurchasedOnly,
    tagsOperator
  ] as QueryKey;
}

/**
 * Fetches a list of entities using the `browseEntities` GraphQL query.
 *
 * @param graphqlClient - The GraphQL client used to send the query.
 * @param entityType - The type of entity to browse.
 * @param options - An optional object containing query filters and settings.
 * @param options.liveState - The live state of the entities to browse (optional).
 * @param options.sortField - The field to sort the results by (optional).
 * @param options.sortDirection - The direction to sort the results, either 'ASC' or 'DESC' (optional).
 * @param options.numResults - The number of results to retrieve (defaults to 12).
 * @param options.userEntitlements - The entitlements for the user.
 * @returns A query configuration object for use with `useSuspenseQuery` or `prefetchQuery`.
 */
export function browseEntities(
  graphqlClient: GraphqlClient,
  entityType: EntityType,
  options: BrowseEntitiesOptions = {}
): UseSuspenseQueryOptions<
  BrowseEntities | null,
  DefaultError,
  BrowseEntities | null,
  QueryKey
> {
  const {
    liveState,
    tags,
    sortField,
    sortDirection,
    userEntitlements,
    numResults = 12,
    pageStart = 0,
    includePurchasedOnly = false,
    tagsOperator
  } = options;

  const queryKey = browseEntitiesQueryKey(entityType, {
    liveState,
    tags,
    sortField,
    sortDirection,
    userEntitlements,
    numResults,
    pageStart,
    includePurchasedOnly,
    tagsOperator
  });

  return {
    queryKey,
    queryFn: async () => {
      const { data, errors } = await graphqlClient.queries.browseEntities({
        entityType,
        liveState,
        tags,
        tagsOperator,
        pageSize: numResults,
        pageStart,
        sortField,
        sortDirection,
        userEntitlements,
        includePurchasedOnly
      });

      if (errors && errors.length) {
        const errorMsg = `Error calling browseEntities: ${errors.map((e) => e.message).join(', ')}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      return data ?? null;
    }
  };
}

export function getBrowseEntitiesInfiniteQueryKey(
  entityType: EntityType,
  options: BrowseEntitiesOptions
): QueryKey {
  return ['browseEntities', entityType, options];
}

export function browseEntitiesInfiniteQueryFn(
  graphqlClient: GraphqlClient,
  entityType: EntityType,
  options: BrowseEntitiesOptions = {}
) {
  return async ({ pageParam = 0 }) => {
    const pageSize = options.numResults ?? 12;

    const { data, errors } = await graphqlClient.queries.browseEntities({
      entityType,
      liveState: options.liveState,
      tags: options.tags,
      tagsOperator: options.tagsOperator,
      pageSize,
      pageStart: pageParam,
      sortField: options.sortField,
      sortDirection: options.sortDirection,
      userEntitlements: options.userEntitlements
    });

    if (errors && errors.length) {
      const errorMsg = `Error calling browseEntities: ${errors.map((e) => e.message).join(', ')}`;
      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    const nextPageParam =
      data?.hits?.length === pageSize ? pageParam + pageSize : null;

    return {
      data,
      pageParam: nextPageParam
    };
  };
}
